import { currency } from '@/utils';
import { i18n } from '@/locales';
import { reportTypeList, depth2TitleTypeToDepth1List, depth3TitleTypeToDepth2List, expandableDepth2TypeList, expandableDepth1TypeList, costDepth3TypeList, costDepth2TypeList, onlyHubCategoryList, onlyWorkerCategoryList, } from '@/constants/baromoneyReport';
import { domainEnum } from '@/constants/global';
export default class BaromoneyReport {
    transactions;
    balanceAmount;
    tableItems;
    constructor({ transactions, balanceAmount, }) {
        this.transactions = transactions;
        this.balanceAmount = balanceAmount;
        this.tableItems = {
            // dep1
            // 총 손익 (총 수익 - 총 비용)
            total_profit_and_cost: {
                title: 'total_profit_and_cost',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                isTotalSumRow: true,
            },
            // 총 잔액 변동 (총 손익 + 기타)
            total_balance_amount: {
                title: 'total_balance_amount',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                isTotalSumRow: true,
            },
            // dep1+dep2
            // 총 수익
            total_profit: {
                title: 'total_profit',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 총 비용
            total_cost: {
                title: 'total_cost',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 기타
            etc: {
                title: 'etc',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // dep1+dep2+dep3
            // 배달 수익
            delivery_profit: {
                title: 'delivery_profit',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 배달 비용
            delivery_cost: {
                title: 'delivery_cost',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 기타 비용
            etc_cost: {
                title: 'etc_cost',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 라이더 정기공제 +
            regular_payment_for_worker_plus: {
                title: 'regular_payment_for_worker_plus',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
            // 라이더 정기공제 -
            regular_payment_for_worker_minus: {
                title: 'regular_payment_for_worker_minus',
                amount: 0,
                taxAmount: 0,
                totalAmount: 0,
                canExpanded: true,
            },
        };
    }
    // v-data-table items 형식에 맞게 데이터 생성하기
    setTableItems() {
        // 1. transactions type을 테이블 항목 이름으로 묶기
        const transactionsGroupByType = this.transactions.reduce((acc, { type, category, amount, taxAmount, totalAmount, }) => {
            const title = reportTypeList[type];
            if (title) {
                const isPossibleHubCategory = onlyHubCategoryList.some((listTitle) => listTitle === title);
                const isPossibleWorkerCategory = onlyWorkerCategoryList.some((listTitle) => listTitle === title);
                // 각 type이 계산에 필요한 값인지 여부를 category(현재 서버에서는 HUB | WORKER만 사용되고 있음)와의 일치 여부를 통해 확인하기
                // 각 type의 category는 기획문서를 따름
                // 기획문서: https://www.notion.so/barogohq/9362eef24c884ccb919d8620b85a2300?v=7a77bdf453ea4040ab614ea87f488055
                if ((isPossibleHubCategory && category === domainEnum.WORKER)
                    || (isPossibleWorkerCategory && category === domainEnum.HUB)) {
                    return acc;
                }
                if (!acc[title]) {
                    acc[title] = {
                        title,
                        amount: 0,
                        taxAmount: 0,
                        totalAmount: 0,
                    };
                }
                acc[title].amount += amount;
                acc[title].taxAmount += taxAmount;
                acc[title].totalAmount += totalAmount;
            }
            return acc;
        }, {});
        // 2. expandable depth3 항목을 depth2 항목으로 묶기
        const transactionsGroupByDepth2Title = Object.values(transactionsGroupByType)
            .reduce((acc, cur) => {
            if (!cur) {
                return acc;
            }
            const { title, amount, taxAmount, totalAmount, } = cur;
            if (amount === 0 && taxAmount === 0 && totalAmount === 0) {
                return acc;
            }
            const depth2Title = depth3TitleTypeToDepth2List[title];
            if (depth2Title) {
                if (!acc[depth2Title]) {
                    acc[depth2Title] = [];
                }
                acc[depth2Title].push({
                    title,
                    amount,
                    taxAmount,
                    totalAmount,
                });
                if (!this.tableItems[depth2Title]) {
                    this.tableItems[depth2Title] = {
                        title,
                        amount,
                        taxAmount,
                        totalAmount,
                    };
                    return acc;
                }
                this.tableItems[depth2Title].amount += amount;
                this.tableItems[depth2Title].taxAmount += taxAmount;
                this.tableItems[depth2Title].totalAmount += totalAmount;
            }
            return acc;
        }, {});
        // 3. expandable depth3 항목을 totalAmount 기준 내림차순으로 정렬하기
        Object.values(transactionsGroupByType)
            .forEach((item) => {
            if (!item) {
                return;
            }
            const { title, amount, taxAmount, totalAmount, } = item;
            if (amount === 0 && taxAmount === 0 && totalAmount === 0) {
                return;
            }
            const depth2Title = depth3TitleTypeToDepth2List[title];
            // 비용 항목은 기본 음수값으로 생각할 때 합계액의 절대값 기준으로 내림차순 정렬
            const isCostType = costDepth3TypeList.some((costTitle) => costTitle === title);
            if (isCostType) {
                transactionsGroupByDepth2Title[depth2Title]
                    .sort((a, b) => a.totalAmount - b.totalAmount);
            }
            else {
                transactionsGroupByDepth2Title[depth2Title]
                    .sort((a, b) => b.totalAmount - a.totalAmount);
            }
        });
        let transactionsGroupByResult = [];
        if (Object.entries(transactionsGroupByDepth2Title).length > 0) {
            // 4. expandable depth2 항목을 depth1 항목으로 묶기
            Object.entries(transactionsGroupByDepth2Title)
                .forEach(([key, value,]) => {
                const depth1Title = depth2TitleTypeToDepth1List[key];
                const isExpandableDepth2Type = expandableDepth2TypeList
                    .find((title) => title === key);
                const isExpandableDepth1Type = expandableDepth1TypeList
                    .find((title) => title === depth1Title);
                const { amount, taxAmount, totalAmount, } = this.tableItems[key];
                this.tableItems[depth1Title].amount += amount;
                this.tableItems[depth1Title].taxAmount += taxAmount;
                this.tableItems[depth1Title].totalAmount += totalAmount;
                if (isExpandableDepth1Type) {
                    this.tableItems[depth1Title].depth2 = [];
                }
                if (isExpandableDepth2Type) {
                    this.tableItems[key].depth3 = value;
                }
            });
            // 5. expandable depth2 항목을 totalAmount기준 내림차순으로 정렬하기
            Object.entries(transactionsGroupByDepth2Title)
                .forEach(([key,]) => {
                const depth1Title = depth2TitleTypeToDepth1List[key];
                if (this.tableItems[depth1Title].depth2) {
                    this.tableItems[depth1Title].depth2.push(this.tableItems[key]);
                    // 비용 항목은 기본 음수값으로 생각할 때 합계액의 절대값 기준으로 내림차순 정렬
                    const isCostType = costDepth2TypeList.some((costTitle) => costTitle === key);
                    if (isCostType) {
                        this.tableItems[depth1Title].depth2
                            .sort((a, b) => a.totalAmount - b.totalAmount);
                    }
                    else {
                        this.tableItems[depth1Title].depth2
                            .sort((a, b) => b.totalAmount - a.totalAmount);
                    }
                }
            });
            const { total_profit: totalProfit, total_cost: totalCost, total_profit_and_cost: totalProfitAndCost, etc, total_balance_amount: totalBalanceAmount, } = this.tableItems;
            // 6. summery rows 계산하기
            // 총 손익 (총 수익 - 총 비용) 계산하기
            totalProfitAndCost.amount = totalProfit.amount + totalCost.amount;
            totalProfitAndCost.taxAmount = totalProfit.taxAmount + totalCost.taxAmount;
            totalProfitAndCost.totalAmount = totalProfit.totalAmount + totalCost.totalAmount;
            // 총 잔액 변동 (총 손익 + 기타) 계산하기
            totalBalanceAmount.amount = totalProfitAndCost.amount + etc.amount;
            totalBalanceAmount.taxAmount = totalProfitAndCost.taxAmount + etc.taxAmount;
            totalBalanceAmount.totalAmount = totalProfitAndCost.totalAmount + etc.totalAmount;
            // 7. 사용자에게 보여질 금액 포맷 적옹하기
            let formattedProfitList = [];
            // Depth1 expandable임에도 공급가액, 세액, 합계액 모두 0인 경우는 숨기기
            if (totalProfit.depth2?.length > 0) {
                // 총 수익 항목 title locale 적용 및 금액 관련 항목 currency 적용
                formattedProfitList = this.formattedAmount([
                    totalProfit,
                ], {
                    signDisplay: 'negative',
                    maximumFractionDigits: 2,
                });
            }
            let formattedCostList = [];
            // Depth1 expandable임에도 공급가액, 세액, 합계액 모두 0인 경우는 숨기기
            if (totalCost.depth2?.length > 0) {
                // 총 비용 항목 title locale 적용 및 금액 관련 항목 currency 적용
                formattedCostList = this.formattedAmount([
                    totalCost,
                ], {
                    signDisplay: 'never',
                    maximumFractionDigits: 2,
                });
            }
            const etcAndTotalRawList = [
                totalProfitAndCost,
                totalBalanceAmount,
            ];
            // Depth1 expandable임에도 공급가액, 세액, 합계액 모두 0인 경우는 숨기기
            if (etc.depth2?.length > 0) {
                etcAndTotalRawList.splice(1, 0, etc);
            }
            // 기타, 총 손익, 총 잔액 변동 title locale 적용 및 금액 관련 항목 currency 적용
            const formattedEtcAndTotalRawList = this.formattedAmount(etcAndTotalRawList, {
                signDisplay: 'exceptZero',
                maximumFracSignificantts: 2,
            });
            transactionsGroupByResult = [
                ...formattedProfitList,
                ...formattedCostList,
                ...formattedEtcAndTotalRawList,
            ];
        }
        // TODO 서버에서 주는 잔액 관련 항목 금액이 불일치하여 임시로 숨김처리
        // 관련 슬랙: https://barogo.slack.com/archives/C01H0SHLVBQ/p1676012218590109
        // const lc = 'views.baromoney_report.';
        // const lcTableItem = `${lc}table_item.`;
        // const {
        //   openBalanceAmount,
        //   closeBalanceAmount,
        // } = this.balanceAmount;
        // 8. 잔액 관련 항목 추가하기
        // const result = transactionsGroupByResult.concat(
        //   [
        //     // 전기 잔액
        //     {
        //       title: i18n.t(`${lcTableItem}open_balance_amount`),
        //       totalAmount: currency(openBalanceAmount, {
        //         maximumFractionDigits: 2,
        //       }),
        //     },
        //     // 마감 잔액
        //     {
        //       title: i18n.t(`${lcTableItem}close_balance_amount`),
        //       totalAmount: currency(closeBalanceAmount, {
        //         maximumFractionDigits: 2,
        //       }),
        //     }],
        // );
        return transactionsGroupByResult;
    }
    /**
     * 음수값의 비용인 경우 - 기호 붙이기
     * @param cost 원래 금액
     * @param currencyOption 화폐 단위 옵션(utils currency의 options)
     */
    // eslint-disable-next-line class-methods-use-this
    checkedCostSignReverse = (cost, currencyOption) => {
        const { signDisplay } = currencyOption;
        if (signDisplay === 'never' && cost > 0) {
            return `-${currency(cost, currencyOption)}`;
        }
        return currency(cost, currencyOption);
    };
    /**
     * 바로머니 리포트 테이블 데이터 타이틀에 locale 적용, 숫자값 기호 여부, 화폐단위 붙이기 등 포멧하기
     * @param {Object} list 포멧 전 바로머니 리포트 테이블 데이터
     * @param {Object} currencyOption 화폐 단위 옵션(utils currency의 options)
     * @returns {Array}
     */
    formattedAmount = (list, currencyOption = {}) => list.map((item) => {
        const lc = 'views.baromoney_report.';
        const lcTableItem = `${lc}table_item.`;
        let formattedDepth2List = null;
        if (item.canExpanded) {
            let formattedDepth3List = null;
            formattedDepth2List = item.depth2.map((depth2Item) => {
                if (depth2Item.canExpanded) {
                    formattedDepth3List = depth2Item.depth3.map((depth3Item) => {
                        const { title, amount, taxAmount, totalAmount, } = depth3Item;
                        return {
                            title: i18n.t(`${lcTableItem}${title}`),
                            amount: this.checkedCostSignReverse(amount, currencyOption),
                            taxAmount: this.checkedCostSignReverse(taxAmount, currencyOption),
                            totalAmount: this.checkedCostSignReverse(totalAmount, currencyOption),
                        };
                    });
                }
                const { title, amount, taxAmount, totalAmount, canExpanded, } = depth2Item;
                return {
                    title: i18n.t(`${lcTableItem}${title}`),
                    amount: this.checkedCostSignReverse(amount, currencyOption),
                    taxAmount: this.checkedCostSignReverse(taxAmount, currencyOption),
                    totalAmount: this.checkedCostSignReverse(totalAmount, currencyOption),
                    canExpanded,
                    depth3: formattedDepth3List,
                };
            });
        }
        const { title, amount, taxAmount, totalAmount, canExpanded, isTotalSumRow, } = item;
        return {
            title: i18n.t(`${lcTableItem}${title}`),
            amount: this.checkedCostSignReverse(amount, currencyOption),
            taxAmount: this.checkedCostSignReverse(taxAmount, currencyOption),
            totalAmount: this.checkedCostSignReverse(totalAmount, currencyOption),
            canExpanded,
            isTotalSumRow,
            depth2: formattedDepth2List,
        };
    });
}
